import React, { useContext, useMemo } from "react";
import { Contact } from "../types/User";
import { ModalsContext } from "./providers/ModalsProvider";
import GearButton from "../ui/gearbutton";

interface ProfileContactsProps {
  contacts?: Contact[];
}

const getContactIcon = (type: Contact["type"]): string => {
  const iconMap: Record<Contact["type"], string> = {
    email: "/icons/socials/email.svg",
    instagram: "/icons/socials/instagram.svg",
    x: "/icons/socials/x.svg",
    linkedin: "/icons/socials/linkedin.svg",
    facebook: "/icons/socials/facebook.svg",
    website: "/icons/socials/website.svg",
    telegram: "/icons/socials/telegram.svg",
  };
  return iconMap[type];
};

const formatContactUrl = (contact: Contact): string => {
  const { type, value } = contact;
  
  if (type === "email") {
    return `mailto:${value}`;
  }
  
  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }
  
  return `https://${value}`;
};

const openContact = (contact: Contact) => {
  const url = formatContactUrl(contact);
  window.open(url, "_blank", "noopener,noreferrer");
};

const ProfileContacts: React.FC<ProfileContactsProps> = ({ contacts }) => {
  const ctx = useContext(ModalsContext);
  if (!ctx) {
    throw new Error('ProfileContacts deve essere usato dentro ModalsProvider');
  }
  const { openContactSelector } = ctx;

  const groupedContacts = useMemo(() => {
    if (!contacts || contacts.length === 0) return {};
    
    return contacts.reduce((acc, contact) => {
      if (!acc[contact.type]) {
        acc[contact.type] = [];
      }
      acc[contact.type].push(contact);
      return acc;
    }, {} as Record<Contact["type"], Contact[]>);
  }, [contacts]);

  const contactTypes = Object.keys(groupedContacts) as Contact["type"][];

  if (contactTypes.length === 0) {
    return null;
  }

  const handleContactClick = (type: Contact["type"], contactsOfType: Contact[]) => {
    if (contactsOfType.length === 1) {
      openContact(contactsOfType[0]);
    } else if (openContactSelector) {
      openContactSelector(contactsOfType, type);
    }
  };

  return (
    <div className="flex items-center gap-2 mt-1 flex-wrap">
      {contactTypes.map((type) => {
        const contactsOfType = groupedContacts[type];
        return (
          <GearButton
            variant="ghost"
            size="small"
            key={type}
            style={{padding: '0px'}}
            onClick={() => handleContactClick(type, contactsOfType)}
            title={type}
            type="button"
          >
            <img
              src={getContactIcon(type)}
              alt={type}
              className="w-[14px] h-[14px] opacity-80 group-hover:opacity-100 transition-opacity"
            />
          </GearButton>
        );
      })}
    </div>
  );
};

export default ProfileContacts;

